import classNames from 'classnames';
import Immutable from 'immutable';
import Ssi from 'modules/SSIAnchors/components/SSI';
import React from 'react';
import { ConnectDropTarget } from 'react-dnd';
import ImagesLoadingObserver from 'react-on-images-loaded';
import { ProjectTypeContext } from 'App';
import HeightExceedingIndicator from 'components/HeightExceedingIndicator';
import Section from 'components/Section';
import { ProjectsConfig } from 'const';
import * as ArtboardsModels from 'containers/Artboards/models';
import { sectionsHeight as sectionsHeightSelector } from 'containers/ScreenDefinitions/selectors';
import * as Models from 'models';
import { backgroundColorFromRelation as getBackgroundColorFromSource } from 'utils/converters';
import { isVerticalSSIPosition } from 'utils/ssi/ssiPosition';
import { getBackgroundColor, getBackgroundImage } from 'utils/styles';
import { toPx } from 'utils/toPx';
import styles from './styles.module.scss';

type Props = {
  areImagesBeingDownloaded: boolean;
  artboard: Models.ArtboardMap;
  artboardImagesLoaded: ArtboardsModels.ActionCreator.ArtboardImagesLoaded;
  artboardMounted: ArtboardsModels.ActionCreator.ArtboardMounted;
  artboardWillBeMounted: ArtboardsModels.ActionCreator.ArtboardWillBeMounted;
  colors: Models.BrandColorsList;
  images: Models.ImagesMap;
  layoutsBySectionId: Models.LayoutsBySectionId;
  sections: Models.ExtendedSectionsList;
  sectionsHeight: ReturnType<typeof sectionsHeightSelector>;
  sectionsWidth: Models.MasterScreen.SectionsWidthMap;
  width: number;
  connectDropTarget: ConnectDropTarget;
  isOver: boolean;
};

class Artboard extends React.PureComponent<Props> {
  private layoutsRefs = Immutable.Map<string, HTMLDivElement>();

  componentWillMount(): void {
    this.props.artboardWillBeMounted();
  }

  componentDidMount(): void {
    this.props.artboardMounted();
  }

  componentDidUpdate(): void {
    this.props.artboardMounted();
  }

  componentWillUpdate(nextProps: Props): void {
    this.props.artboardWillBeMounted();

    const currentArtboardId = this.props.artboard.get('id');
    const nextArtboardId = nextProps.artboard.get('id');

    if (currentArtboardId !== nextArtboardId) {
      this.layoutsRefs = Immutable.Map();
    }
  }

  private readonly saveLayoutRef = (layoutId: string, element: HTMLDivElement): void => {
    this.layoutsRefs = element ? this.layoutsRefs.set(layoutId, element) : this.layoutsRefs.delete(layoutId);
  };

  private readonly getLayoutsRefs = (): Immutable.Map<string, HTMLDivElement> => this.layoutsRefs;

  private readonly renderSection = (section: Models.ExtendedSectionMap, index: number): JSX.Element => {
    const { layoutsBySectionId, sectionsHeight } = this.props;
    const sectionId = section.get('id');
    const sectionName = section.get('name');

    return (
      <Section
        section={section}
        layouts={layoutsBySectionId.get(sectionId)}
        sectionHeight={sectionsHeight.get(sectionName)}
        key={sectionId}
        saveLayoutRef={this.saveLayoutRef}
        getLayoutsRefs={this.getLayoutsRefs}
        dataTestId={index}
      />
    );
  };

  private readonly renderSections = (): Immutable.List<JSX.Element> | JSX.Element => {
    const { sections, artboard, sectionsHeight, sectionsWidth } = this.props;

    return sections.map((section, index) => {
      const ssi = artboard.get('ssi');
      const sectionName = section.get('name');

      if (ssi?.get('section') !== sectionName) {
        return this.renderSection(section, index);
      }

      return (
        <div
          className={classNames({ [styles.verticalWrapper]: isVerticalSSIPosition(ssi.get('position')) })}
          key={`${artboard.get('id')}${sectionName}`}
        >
          {this.renderSection(section, index)}
          <Ssi
            section={section}
            sectionHeight={sectionsHeight.get(sectionName)}
            sectionWidth={sectionsWidth.get(sectionName)}
            dataTestId={index ?? 0}
          />
        </div>
      );
    });
  };

  private readonly onImagesLoaded = (): void => {
    this.props.artboardImagesLoaded();
  };

  render(): React.ReactNode {
    const { artboard, colors, images, width, connectDropTarget, isOver, areImagesBeingDownloaded } = this.props;
    const backgroundImage = artboard.getIn(['styles', 'backgroundImage']);
    const height = artboard.getIn(['styles', 'height']);
    const backgroundColorFromSource = getBackgroundColorFromSource(colors, artboard);
    const backgroundInlineStyles = backgroundColorFromSource ? getBackgroundColor(backgroundColorFromSource) : {};

    return connectDropTarget((
      <div
        className={styles.Artboard}
        style={{
          width: toPx(width),
          ...getBackgroundImage(backgroundImage, images),
          ...backgroundInlineStyles,
        }}
      >
        <ImagesLoadingObserver
          onLoaded={this.onImagesLoaded}
          // HACK: ImagesLoadingObserver initiates its logic on componentDidMount,
          // 1) artboard id - is needed as part of key to wait images loading after switching between screens
          // 2) areImagesBeingDownloaded - while images' renditions are being loaded there are no <img />
          // tags within the artboard and nothing to wait, <img/> tags will be created after images' renditions are loaded
          // since areImagesBeingDownloaded is a part of key, ImagesLoadingObserver will be waiting images after they are complete loaded.
          key={`${artboard.get('id')}:${!!areImagesBeingDownloaded}`}
        >
          {this.renderSections()}
          <ProjectTypeContext.Consumer>
            {(projectType): React.ReactNode => {
              const { areScreensResizable } = ProjectsConfig[projectType];
              if (!areScreensResizable || !height) {
                return null;
              }

              return (
                <HeightExceedingIndicator
                  offsetTop={height}
                  isShown={true}
                />
              );
            }}
          </ProjectTypeContext.Consumer>
        </ImagesLoadingObserver>
        {isOver && <div className={styles.overlay} />}
      </div>
    ));
  }
}

export default Artboard;
